// src/services/data/fetchers/transformations.ts
/**
 * Pure transformation functions for chart data.
 * Used by both assistant charts and dashboard providers.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface TimeSeriesData {
  dates: string[];
  series: Array<{ name: string; values: number[] }>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Date Parsing
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Parse a YYYYMMDD integer or string to UTC timestamp
 */
export function parseDateInt(dateInt: number | string): number {
  const str = String(dateInt);
  const year = parseInt(str.slice(0, 4), 10);
  const month = parseInt(str.slice(4, 6), 10) - 1;
  const day = parseInt(str.slice(6, 8), 10);
  return Date.UTC(year, month, day);
}

/**
 * Parse timeframe string (e.g., "7d", "30d", "1y") to number of days
 */
export function parseTimeframeToDays(timeframe?: string): number | null {
  if (!timeframe) return null;
  const match = timeframe.match(/^(\d+)(d|w|m|y)$/i);
  if (!match) return null;
  
  const value = parseInt(match[1]!, 10);
  const unit = match[2]!.toLowerCase();
  
  switch (unit) {
    case 'd': return value;
    case 'w': return value * 7;
    case 'm': return value * 30;
    case 'y': return value * 365;
    default: return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Time Series Transformations
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Filter time series data to a specific timeframe
 */
export function filterByTimeframe(
  dates: string[],
  series: Array<{ name: string; values: number[] }>,
  timeframeDays: number | null
): TimeSeriesData {
  if (timeframeDays === null || dates.length === 0) {
    return { dates, series };
  }
  
  const cutoffTs = Date.now() - (timeframeDays * 24 * 60 * 60 * 1000);
  let startIdx = 0;
  
  for (let i = 0; i < dates.length; i++) {
    if (parseDateInt(dates[i]!) >= cutoffTs) {
      startIdx = i;
      break;
    }
  }
  
  return {
    dates: dates.slice(startIdx),
    series: series.map(s => ({
      name: s.name,
      values: s.values.slice(startIdx),
    })),
  };
}

/**
 * Filter and normalize time series for comparison charts (index to 100)
 */
export function filterAndNormalizeForComparison(
  dates: string[],
  series: Array<{ name: string; values: number[] }>,
  timeframeDays: number | null
): TimeSeriesData {
  let startIdx = 0;
  
  if (timeframeDays !== null && dates.length > 0) {
    const cutoffTs = Date.now() - (timeframeDays * 24 * 60 * 60 * 1000);
    for (let i = 0; i < dates.length; i++) {
      if (parseDateInt(dates[i]!) >= cutoffTs) {
        startIdx = i;
        break;
      }
    }
  }
  
  const filteredDates = dates.slice(startIdx);
  
  const normalizedSeries = series.map(s => {
    const slicedValues = s.values.slice(startIdx);
    const firstValidIdx = slicedValues.findIndex(v => v !== 0 && v !== null && v !== undefined);
    const firstValidValue = firstValidIdx >= 0 ? slicedValues[firstValidIdx] : null;
    
    const normalizedValues = slicedValues.map((v, idx) => {
      if (firstValidIdx < 0 || !firstValidValue) return 0;
      if (idx < firstValidIdx) return 0;
      if (v === 0 || v === null || v === undefined) return 0;
      return (v / firstValidValue) * 100;
    });
    
    return { name: s.name, values: normalizedValues };
  });
  
  return { dates: filteredDates, series: normalizedSeries };
}

// ─────────────────────────────────────────────────────────────────────────────
// Data Alignment
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Align multiple series to a common date axis
 */
export function alignSeriesToCommonDates(
  seriesData: Array<{
    name: string;
    dates: (string | number)[];
    values: (number | null)[];
  }>
): TimeSeriesData {
  if (seriesData.length === 0) {
    return { dates: [], series: [] };
  }

  // Build date maps for each series
  const dateMaps: Array<{ name: string; dateMap: Map<string, number> }> = [];
  const allDatesSet = new Set<string>();
  
  for (const data of seriesData) {
    const dateMap = new Map<string, number>();
    
    for (let i = 0; i < data.dates.length; i++) {
      const dateStr = String(data.dates[i]);
      const value = data.values[i];
      if (typeof value === 'number' && !isNaN(value) && value !== 0) {
        dateMap.set(dateStr, value);
        allDatesSet.add(dateStr);
      }
    }
    
    dateMaps.push({ name: data.name, dateMap });
  }
  
  // Sort dates
  const allDates = Array.from(allDatesSet).sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
  
  if (allDates.length === 0) {
    return { dates: [], series: [] };
  }
  
  // Build aligned series
  const series = dateMaps.map(({ name, dateMap }) => ({
    name,
    values: allDates.map(date => dateMap.get(date) ?? 0),
  }));
  
  return { dates: allDates, series };
}

// ─────────────────────────────────────────────────────────────────────────────
// Value Extraction
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Extract windowed value from metrics array
 */
export function extractWindowedValue(
  metrics: Array<{ metric_key: string; windows?: Record<string, { value?: number | null }> }>,
  metricKey: string,
  window: string
): number {
  const metricData = metrics.find((m) => m.metric_key === metricKey);
  return metricData?.windows?.[window]?.value ?? 0;
}

// ─────────────────────────────────────────────────────────────────────────────
// Sorting
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Sort categories and values by value descending
 */
export function sortByValueDescending<T extends { categories: string[]; values: number[] }>(
  data: T
): T {
  const sortedIndices = data.values
    .map((v, i) => ({ v, i }))
    .sort((a, b) => b.v - a.v)
    .map(x => x.i);
  
  return {
    ...data,
    categories: sortedIndices.map(i => data.categories[i]!),
    values: sortedIndices.map(i => data.values[i]!),
  };
}

